interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export type CoalesceFn<TPayload> = (
  existing: TPayload,
  incoming: TPayload,
) => TPayload | null;

export interface SyncQueueOptions<TPayload> {
  storage: StorageLike;
  storageKey: string;
  // Called for each existing entry when a new one arrives. Return a merged
  // replacement to coalesce (the existing slot is overwritten and the new
  // entry is not appended), or null to leave the existing entry untouched
  // and keep scanning. If no entry coalesces, the new entry is appended.
  coalesce: CoalesceFn<TPayload>;
}

export class SyncQueue<TPayload> {
  private entries: TPayload[] = [];
  // Entries this queue has REMOVED — dequeued after a successful send, or
  // replaced by a coalescing enqueue. Needed only to tell "another tab wrote
  // this" apart from "I already dealt with this" when merging on persist; see
  // `persist`. Bounded by what this session actually handled.
  private readonly handled = new Set<string>();

  constructor(private readonly opts: SyncQueueOptions<TPayload>) {
    this.load();
  }

  size(): number {
    return this.entries.length;
  }

  peek(): TPayload | null {
    return this.entries[0] ?? null;
  }

  enqueue(entry: TPayload): void {
    for (let i = 0; i < this.entries.length; i += 1) {
      const existing = this.entries[i];
      const merged = this.opts.coalesce(existing, entry);

      if (merged != null) {
        this.handled.add(identity(existing));
        this.entries[i] = merged;
        this.persist();
        return;
      }
    }

    this.entries.push(entry);
    this.persist();
  }

  dequeue(): TPayload | null {
    const head = this.entries.shift() ?? null;

    if (head != null) {
      this.handled.add(identity(head));
      this.persist();
    }

    return head;
  }

  private load(): void {
    this.entries = this.readStored();
  }

  /**
   * Take over everything queued for this user, and re-assert it into storage.
   *
   * The merge in `persist` reads and then writes, and those two steps are not
   * one operation across TABS: two queues can both read the same array before
   * either writes, and the later write still drops the other's entry. Closing
   * that properly means a cross-tab lock (`navigator.locks`), which is async —
   * while enqueue/dequeue are synchronous, called from event handlers and from
   * the worker's commit path, and making them async would change what every
   * caller can assume about ordering. Not a change to make in passing.
   *
   * What that leaves is a microsecond-wide window between two simultaneous
   * writes, and it only costs anything if the losing tab never writes again —
   * its own memory still holds the entry and still sends it. So the worker
   * calls this each tick: a clobbered entry is back in storage within one
   * interval, and the loss needs the tab to die inside that window.
   *
   * ADOPTING is the other half, and the reason this isn't just a re-persist.
   * Preserving another tab's entries keeps them safe but sends nobody: close
   * the tab that queued them and they sit in storage until some tab reloads.
   * So each tick also claims whatever is queued but unowned. Two live tabs
   * then both hold it and both send it, which the server's last-write-wins
   * makes a no-op for the loser — the same bargain the successor-queue rule
   * already takes, and much the cheaper mistake than an orphan that never
   * drains.
   *
   * Claimed entries go in FRONT, and deliberately not through `enqueue`:
   * coalescing there keeps the INCOMING entry, which for an adopted write is
   * the older one, and it would replace a newer verdict this tab is holding.
   * Sending both in age order lets the server's clock decide, which is the
   * only thing that can.
   */
  reconcile(): void {
    const orphans = this.foreignEntries();

    if (orphans.length > 0) {
      this.entries = [...orphans, ...this.entries];
    }

    if (this.entries.length === 0) {
      return;
    }

    this.persist();
  }

  /**
   * Write this queue's entries WITHOUT dropping anyone else's.
   *
   * The storage key is per-user, not per-tab, so two tabs of the same app hold
   * independent queues over one key. Each loads storage once and then writes
   * its whole array, so the second writer erased the first's — a mark made in
   * one tab while offline was durably stored and then silently gone by the
   * time it should have drained. (Native has one WebView and never saw this;
   * the To Do rail persists on web too, which is where it bites.)
   *
   * So a write preserves anything in storage this queue neither holds nor has
   * already handled: by definition another tab put it there, and that tab's
   * worker will drain it. Recomputed from storage every time rather than
   * cached, so once the owning tab removes an entry it stays removed instead
   * of being resurrected here. Identity is the serialized payload — two
   * byte-identical entries are the same write, and the server's
   * last-write-wins settles the pair either way.
   */
  private persist(): void {
    const foreign = this.foreignEntries();
    const next = [...foreign, ...this.entries];

    if (next.length === 0) {
      try {
        this.opts.storage.removeItem(this.opts.storageKey);
      } catch (error) {
        console.warn("[SyncQueue] persist failed clearing storage", error);
      }

      return;
    }

    this.writeWithQuotaGuard(foreign);
  }

  /**
   * Attempt the write, and if storage rejects it (typically a quota error),
   * drop THIS queue's oldest entries — never the foreign ones `foreign`
   * carries in from other tabs — and retry, one drop at a time, until a
   * write succeeds or this queue has nothing left of its own to drop.
   *
   * A drop here is committed to `this.entries` only once the SMALLER write
   * actually lands: a storage that rejects every write (not just large ones)
   * must never shrink the in-memory queue, or a page whose storage is simply
   * broken would silently lose queued work it could still send over the
   * network. Dropped entries are not added to `handled` — they were never
   * delivered, so another tab must still be able to adopt them later.
   *
   * Once this queue's own entries are exhausted, giving up must never touch
   * storage — no `removeItem`, and no write of an empty candidate — however
   * that exhaustion is reached: with nothing foreign left to try either
   * (checked up front, before ever forming a doomed empty candidate), or
   * after a last foreign-only write is itself attempted and fails (caught
   * below). Storage may still durably hold an earlier successful write of
   * this queue's own (say, just the entries before the one that couldn't fit
   * arrived); overwriting or clearing it on the way out would be strictly
   * worse than the pre-guard behavior of leaving a failed write's target
   * untouched.
   */
  private writeWithQuotaGuard(foreign: TPayload[]): void {
    let dropped = 0;
    let loggedFailure = false;

    for (;;) {
      if (dropped >= this.entries.length && foreign.length === 0) {
        // Nothing of this queue's own is left to try, and there is no
        // foreign content to fall back to writing alone. Leave storage
        // exactly as it durably is; `entries` stays untouched too, so what
        // remains in memory can still drain later.
        return;
      }

      const candidate = [...foreign, ...this.entries.slice(dropped)];

      try {
        this.opts.storage.setItem(
          this.opts.storageKey,
          JSON.stringify(candidate),
        );

        if (dropped > 0) {
          this.entries = this.entries.slice(dropped);
        }

        return;
      } catch (error) {
        if (!loggedFailure) {
          loggedFailure = true;
          console.warn(
            "[SyncQueue] persist failed; dropping oldest queued entries",
            error,
          );
        }

        if (dropped >= this.entries.length) {
          // This queue has nothing left of its own to drop, and even the
          // foreign-only write (the `candidate` above was non-empty) still
          // fails. Leave storage as it durably is rather than spin forever.
          return;
        }

        dropped += 1;
      }
    }
  }

  private foreignEntries(): TPayload[] {
    const mine = new Set(this.entries.map(identity));

    return this.readStored().filter((entry) => {
      const key = identity(entry);
      return !mine.has(key) && !this.handled.has(key);
    });
  }

  private readStored(): TPayload[] {
    const raw = this.opts.storage.getItem(this.opts.storageKey);

    if (raw == null) {
      return [];
    }

    try {
      const parsed: unknown = JSON.parse(raw);
      return Array.isArray(parsed) ? (parsed as TPayload[]) : [];
    } catch {
      return [];
    }
  }
}

/** Serialized payload, used only to compare entries across tabs. */
function identity(entry: unknown): string {
  return JSON.stringify(entry);
}
